/** The single authority holder.
 *
 * One `GameState`, one `executeCommand`, one event queue, one version counter.
 * The Pixi presenter and every React panel read through this object and nothing
 * else mutates state - which is what makes "the renderer decides nothing" (I10)
 * enforceable rather than aspirational: the renderer is handed a
 * `PlayerObservation` and a `GameEvent[]` and has no other door.
 *
 * `check()` deserves a note. The UI may not call a rule module, so the host
 * answers "would this be refused, and why" on its behalf. The only honest way
 * to answer is to ask the kernel, and `executeCommand` is the kernel's answer;
 * the result is thrown away and the reason kept. That is a deep clone per probe,
 * so the answers are memoised per state version - a build dialog asks about
 * nineteen buildings on every render and the version does not move between them.
 */

import type {
  ContentRegistry, CreatureDefId, ExecuteError, GameCommand, GameEvent, GameState,
  HeroId, HeroState, ObjectId, PathLeg, PlayerId, PlayerObservation, PortalLink,
  PuzzleView, Reason, ReachableSet, TownId,
} from "@game/core";
import {
  cellIndex, caravanDays, computeReachable, dailyMovement, entityKeys, executeCommand,
  makeCtx, maxMana, moveRulesFor, objectSource, observe, pathLegs, puzzleView,
  townSources, validateCaravan,
} from "@game/core";
import {
  aimAtMouth, commandKey, knownLinks, learnTransits, mouthCell, newPortalMemory,
  portalMouths, type PortalMemory,
} from "@game/ai";

export interface Refusal {
  readonly command: GameCommand;
  readonly reason: Reason | null;
  readonly issues: readonly string[];
}

export interface DispatchOutcome {
  readonly ok: boolean;
  readonly events: readonly GameEvent[];
  readonly refusal: Refusal | null;
}

const NO_EVENTS: readonly GameEvent[] = Object.freeze([]);

/** A column already walking, for the "on the road" list. */
export interface CaravanInFlight {
  readonly id: string;
  readonly creature: CreatureDefId;
  readonly count: number;
  /** The destination town's name, not its id: this goes on a screen. */
  readonly to: string;
  readonly daysLeft: number;
}

/** One line of the caravan dialog: a place that could send, and what it holds. */
export interface CaravanOffer {
  readonly key: string;
  /** What to print in the "from" column. */
  readonly from: string;
  readonly creature: CreatureDefId;
  /** The most the rules would accept right now, purse and slots included. */
  readonly max: number;
  /** What the source holds, whether or not it can all be afforded. */
  readonly stock: number;
  readonly days: number;
  readonly fromTown: TownId | null;
  readonly fromObject: ObjectId | null;
}

function refusalOf(command: GameCommand, e: ExecuteError): Refusal {
  return e.kind === "ILLEGAL"
    ? { command, reason: e.reason, issues: [] }
    : { command, reason: null, issues: e.issues };
}

/**
 * The authority. Everything that reads state reads it here; everything that
 * changes state goes through `execute`.
 */
export class GameSession {
  private state: GameState;
  private readonly content: ContentRegistry;
  private viewer: PlayerId;

  private version = 1;
  private readonly listeners = new Set<() => void>();

  /** Events the presenter has not been shown yet. */
  private queue: GameEvent[] = [];
  /** The whole run, for the debug surface and the after-action report. */
  private readonly log: GameEvent[] = [];

  private cachedObs: PlayerObservation | null = null;
  private cachedObsAt = -1;
  private checks = new Map<string, Reason | null>();
  private checksAt = -1;
  private reach: { hero: HeroId; at: number; set: ReachableSet } | null = null;

  /**
   * Where each human seat's portals come out, learned by walking through them.
   *
   * The observation carries no portal pairing, so the shell keeps the same
   * memory the AI controller keeps (`@game/ai` portals): one per viewer, filled
   * by `learnTransits` on every observation and read by `reachableFor`. A gate
   * nobody has been through is invisible to the planner, exactly as for the AI.
   */
  private readonly portals = new Map<string, PortalMemory>();

  private lastRefusal: Refusal | null = null;
  private notifyQueued = false;

  /**
   * Who hears every accepted command, with the version it produced.
   *
   * This is the AI Worker's feed (D7): the Worker is a replay client and this
   * is the only mutation point, so a listener here sees the whole stream in
   * order and never a command the kernel refused. `check()` runs the kernel
   * too but throws the result away, and does not report.
   */
  private readonly commandListeners = new Set<(command: GameCommand, version: number) => void>();

  constructor(state: GameState, content: ContentRegistry, viewer: PlayerId) {
    this.state = state;
    this.content = content;
    this.viewer = viewer;
  }

  // ------------------------------------------------------------- reading

  getState(): GameState { return this.state; }
  getContent(): ContentRegistry { return this.content; }
  getViewer(): PlayerId { return this.viewer; }
  getVersion(): number { return this.version; }
  getRefusal(): Refusal | null { return this.lastRefusal; }

  /** Referentially stable for a version, which every `useMemo` here keys off. */
  getObservation(): PlayerObservation {
    if (this.cachedObs === null || this.cachedObsAt !== this.version) {
      this.cachedObs = this.learnedObserve(this.viewer);
      this.cachedObsAt = this.version;
    }
    return this.cachedObs;
  }

  observeFor(player: PlayerId): PlayerObservation {
    return player === this.viewer ? this.getObservation() : this.learnedObserve(player);
  }

  setViewer(player: PlayerId): void {
    if (this.viewer === player) return;
    this.viewer = player;
    this.cachedObs = null;
    this.bump();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  // ------------------------------------------------------------- writing

  /** The only mutation point. */
  execute(command: GameCommand): DispatchOutcome {
    const r = executeCommand(this.state, command, this.content, { dev: false });
    if (!r.ok) {
      this.lastRefusal = refusalOf(command, r.error);
      this.bump();
      return { ok: false, events: NO_EVENTS, refusal: this.lastRefusal };
    }
    this.noteDispatch(command);
    this.state = r.value.state;
    this.lastRefusal = null;
    for (const e of r.value.events) { this.queue.push(e); this.log.push(e); }
    for (const l of this.commandListeners) l(command, this.state.version);
    this.bump();
    return { ok: true, events: r.value.events, refusal: null };
  }

  /** Hear every accepted command in order. Returns the unsubscribe. */
  onCommand(listener: (command: GameCommand, version: number) => void): () => void {
    this.commandListeners.add(listener);
    return () => { this.commandListeners.delete(listener); };
  }

  /** Events the presenter has not consumed. Draining is what "since the last
   *  frame it drew" means in the `WorldView` contract. */
  drainEvents(): readonly GameEvent[] {
    if (this.queue.length === 0) return NO_EVENTS;
    const out = this.queue;
    this.queue = [];
    return out;
  }

  peekEvents(): readonly GameEvent[] { return this.queue; }
  eventLog(): readonly GameEvent[] { return this.log; }

  // ------------------------------------------------------------- legality

  check(command: GameCommand): Reason | null {
    if (this.checksAt !== this.version) { this.checks = new Map(); this.checksAt = this.version; }
    const key = commandKey(command);
    const hit = this.checks.get(key);
    if (hit !== undefined || this.checks.has(key)) return hit ?? null;

    const r = executeCommand(this.state, command, this.content, { dev: false });
    const reason = r.ok ? null : (r.error.kind === "ILLEGAL" ? r.error.reason : null);
    this.checks.set(key, reason);
    return reason;
  }

  // ------------------------------------------------- rule outputs the UI needs

  /**
   * Movement and mana ceilings. Both are rule outputs, so the shell may not
   * derive them (I10) and the host hands them over instead.
   */
  capacities(): Readonly<Record<string, { movementMax: number; manaMax: number }>> {
    const c = makeCtx(this.state, this.content);
    const out: Record<string, { movementMax: number; manaMax: number }> = {};
    const player = this.state.players[this.viewer];
    if (player === undefined) return out;
    for (const id of player.heroes) {
      const h: HeroState | undefined = this.state.heroes[id];
      if (h === undefined) continue;
      out[id as string] = { movementMax: dailyMovement(c, h), manaMax: maxMana(c, h) };
    }
    return out;
  }

  /**
   * Everywhere that could send creatures to `town`, and how long each would take.
   *
   * A rule output the shell may not derive (I10): what a source holds, what the
   * purse can carry and how many days the road is are all the kernel's, and the
   * count offered is found by asking `validateCaravan` rather than by
   * reimplementing its arithmetic here. Not memoised - it is computed when a
   * dialog opens, not on every render.
   */
  caravanOffers(town: TownId): readonly CaravanOffer[] {
    const c = makeCtx(this.state, this.content);
    const dest = this.state.towns[town];
    if (dest === undefined || dest.owner !== this.viewer) return [];
    const out: CaravanOffer[] = [];

    const add = (
      fromTown: TownId | null, fromObject: ObjectId | null, label: string,
      at: { readonly x: number; readonly y: number; readonly z: number },
      creature: CreatureDefId, stock: number,
    ): void => {
      if (!validateCaravan(c, town, fromTown, fromObject, creature, 1).ok) return;
      // The largest count the rules still accept. Walked down from the stock
      // rather than solved for, because what limits it - the purse, the pool,
      // a garrison slot - is `validateCaravan`'s business and not this file's.
      let max = 0;
      for (let n = stock; n >= 1; n--) {
        if (validateCaravan(c, town, fromTown, fromObject, creature, n).ok) { max = n; break; }
      }
      if (max <= 0) return;
      out.push({
        key: `${fromTown ?? fromObject ?? ""}|${creature as string}`,
        from: label, creature, max, stock,
        days: caravanDays(at, dest),
        fromTown, fromObject,
      });
    };

    for (const id of entityKeys(this.state.objects)) {
      const src = objectSource(c, id as ObjectId, this.viewer);
      if (src === null) continue;
      const name = this.content.creatures.get(src.creature)?.namePlural ?? "dwelling";
      add(null, id as ObjectId, `${name} — (${String(src.x)}, ${String(src.y)})`, src, src.creature, src.stock);
    }
    for (const id of entityKeys(this.state.towns)) {
      const t = this.state.towns[id];
      if (t === undefined || t.owner !== this.viewer || id === (town as string)) continue;
      for (const src of townSources(c, t)) {
        add(id as TownId, null, t.name, t, src.creature, src.stock);
      }
    }
    out.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    return out;
  }

  /**
   * What the obelisks have uncovered for this player.
   *
   * A rule output the shell may not derive (I10): which piece comes off next is
   * a fact about `rootSeed`, the Grail's own piece is held back to last, and
   * both are `adventure/grail.ts`'s. Not memoised - it is computed when the
   * screen opens, not on every render.
   */
  puzzleView(): PuzzleView {
    return puzzleView(makeCtx(this.state, this.content), this.viewer);
  }

  /**
   * The columns this player has on the road, soonest first.
   *
   * Without it a caravan is invisible between the day it is paid for and the
   * day it lands - the player has spent the gold and has nothing to look at,
   * which is the same "silent gesture" defect the QA sweep exists to find.
   */
  caravansOnTheRoad(): readonly CaravanInFlight[] {
    const out: CaravanInFlight[] = [];
    for (const id of entityKeys(this.state.caravans)) {
      const cv = this.state.caravans[id];
      if (cv === undefined || cv.owner !== this.viewer) continue;
      out.push({
        id: cv.id as string,
        creature: cv.def,
        count: cv.count,
        to: this.state.towns[cv.to]?.name ?? (cv.to as string),
        daysLeft: Math.max(0, cv.arrivesOnDay - this.state.turn.day),
      });
    }
    out.sort((a, b) => a.daysLeft - b.daysLeft || (a.id < b.id ? -1 : 1));
    return out;
  }

  /**
   * The kernel's own Dijkstra pass for one hero, cached per version.
   *
   * Portal-aware like the AI's: links this seat has learned are handed over
   * with the observation's explored mask, so the planner costs the same graph
   * the computer plans on.
   */
  reachableFor(hero: HeroId): ReachableSet | null {
    const h = this.state.heroes[hero];
    if (h === undefined) return null;
    if (this.reach !== null && this.reach.hero === hero && this.reach.at === this.version) {
      return this.reach.set;
    }
    const c = makeCtx(this.state, this.content);
    const origin = cellIndex(this.state.map, h.x, h.y, h.z);
    const links = this.portalLinksFor(this.viewer);
    const explored = this.viewer === "spectator"
      ? undefined
      : this.getObservation().map.visibility;
    const set = explored === undefined
      ? computeReachable(this.state.map, origin, moveRulesFor(c, h), { links })
      : computeReachable(this.state.map, origin, moveRulesFor(c, h), { links, explored });
    this.reach = { hero, at: this.version, set };
    return set;
  }

  /**
   * Cell indices along the cheapest route, excluding the origin.
   *
   * The first leg only: a route through a portal teleports the hero and ends
   * his day on stepping onto the near mouth, so the rest of the recorded path
   * is what tomorrow's order covers, not this one's.
   */
  pathFor(hero: HeroId, x: number, y: number, z: number): readonly number[] {
    return this.pathLegsFor(hero, x, y, z)[0]?.cells ?? [];
  }

  /**
   * The same route, cut at every portal. Each leg but the last ends by stepping
   * onto a mouth, which spends the rest of that day; the following leg is the
   * next order, issued after the jump lands.
   */
  pathLegsFor(hero: HeroId, x: number, y: number, z: number): readonly PathLeg[] {
    const set = this.reachableFor(hero);
    if (set === null) return [];
    return pathLegs(set, cellIndex(this.state.map, x, y, z));
  }

  movementLeft(hero: HeroId): number {
    return this.state.heroes[hero]?.movementLeft ?? 0;
  }

  // ------------------------------------------------------------- internals

  /**
   * Observe a seat and teach its portal memory what the board shows.
   *
   * The same call the AI controller makes per turn: a hero who is not where
   * the last order left him has been through a gate, and that is the one way
   * this planner ever learns where a gate goes.
   */
  private learnedObserve(player: PlayerId): PlayerObservation {
    const obs = observe(this.state, player, this.content);
    if (player !== "spectator") {
      learnTransits(this.memoryFor(player), obs, this.content);
    }
    return obs;
  }

  /** One portal memory per human seat; a spectator learns nothing. */
  private memoryFor(player: PlayerId): PortalMemory {
    const key = player as string;
    let mem = this.portals.get(key);
    if (mem === undefined) {
      mem = newPortalMemory();
      this.portals.set(key, mem);
    }
    return mem;
  }

  /** The links this seat has learned, canonically ordered. Empty until a hero
   *  of its own has come out of a gate. */
  portalLinksFor(player: PlayerId): readonly PortalLink[] {
    if (player === "spectator") return [];
    return knownLinks(this.memoryFor(player));
  }

  /** Portal mouths this seat can see, cell by cell. */
  portalMouthsFor(player: PlayerId): ReadonlyMap<number, string> {
    if (player === "spectator") return new Map<number, string>();
    return portalMouths(this.getObservationFor(player), this.content);
  }

  /** The observation a seat's memory reads, without re-learning. */
  private getObservationFor(player: PlayerId): PlayerObservation {
    return player === this.viewer ? this.getObservation() : observe(this.state, player, this.content);
  }

  /**
   * Remember that a human order ends by stepping onto a portal mouth, so that
   * the next observation - which will find the hero somewhere else entirely -
   * can be read as the jump it was.
   *
   * Called with every order the seat is about to hand the kernel, same as the
   * AI controller's `noteRoute` (controller.ts): the "from" half of a link is
   * what the planner asked for rather than something reconstructed afterwards.
   * A route that ends anywhere else clears the note: the hero is walking, not
   * jumping. `portalMouths` is read off the command owner's own observation,
   * so a spectator's order never teaches a seat anything.
   */
  private noteDispatch(command: GameCommand): void {
    if (command.type === "MOVE_HERO") {
      const owner = this.state.heroes[command.hero]?.owner;
      if (owner === undefined || owner === "spectator") return;
      const last = command.path[command.path.length - 1];
      if (last === undefined) return;
      aimAtMouth(this.memoryFor(owner), command.hero, last, this.portalMouthsFor(owner));
      return;
    }
    // A visit reaches out to the object from beside it rather than walking a
    // path, so there is no last cell to read: the mouth is the object itself.
    if (command.type === "VISIT_OBJECT") {
      const owner = this.state.heroes[command.hero]?.owner;
      if (owner === undefined || owner === "spectator") return;
      const mouth = this.objectMouth(owner, command.object);
      if (mouth === null) return;
      aimAtMouth(this.memoryFor(owner), command.hero, mouth, this.portalMouthsFor(owner));
      return;
    }
    // Answering a portal's CHOOSE prompt jumps from the mouth the prompt names
    // in its resume token - the same object the visit above aimed at.
    if (command.type === "RESPOND_TO_INTERACTION") {
      const pending = this.state.pending;
      if (pending === null || pending.id !== command.id) return;
      const owner = pending.playerId;
      if (owner === "spectator") return;
      const data = pending.resumeToken.data;
      if (data["sub"] !== "PORTAL") return;
      const hero = data["hero"];
      const object = data["object"];
      if (typeof hero !== "string" || typeof object !== "string") return;
      const mouth = this.objectMouth(owner, object as ObjectId);
      if (mouth === null) return;
      aimAtMouth(
        this.memoryFor(owner), hero as HeroId, mouth, this.portalMouthsFor(owner),
      );
    }
  }

  /** The mouth cell of a portal object a seat can see, or null. */
  private objectMouth(owner: PlayerId, object: ObjectId): number | null {
    const found = this.getObservationFor(owner).objects.find((o) => o.id === object);
    if (found === undefined || found.kind !== "portal") return null;
    return mouthCell(this.getObservationFor(owner), this.content, found);
  }

  /**
   * The version moves on every command; the subscribers do not.
   *
   * A computer's turn - and a battle especially - issues hundreds of commands
   * in a burst. Waking React for each one re-renders seven panels and repaints
   * the minimap several hundred times for one visible change, which is what
   * turns "the AI is thinking" into "the page is frozen". The version is bumped
   * synchronously, so anything that reads it sees the truth immediately; the
   * notification is coalesced to one per animation frame.
   */
  private bump(): void {
    this.version += 1;
    if (this.notifyQueued) return;
    this.notifyQueued = true;
    const flush = (): void => {
      this.notifyQueued = false;
      for (const l of this.listeners) l();
    };
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(flush);
    else setTimeout(flush, 0);
  }

  /** Wake the subscribers now, without waiting for the frame. */
  flush(): void {
    this.notifyQueued = false;
    for (const l of this.listeners) l();
  }
}
